/**
 * WHAT: "User Access Report" tab inside Manager Attestations. Reuses the same Cassandra
 *       report DBAs see, pre-filtered to the subset of users this attester is assigned
 *       in the selected campaign. Privilege counts are hyperlinks that open a detail
 *       modal listing the exact rows that were counted — same drill-down behaviour as
 *       the DBA-side Cassandra User Access Report.
 * WHY:  Managers review 30+ users at a time; the per-user "Show Details" drill-down
 *       is slow and kept breaking. A table view with per-cell hyperlinks lets them
 *       see counts at a glance and drill only where the number surprises them.
 *
 * Scope for v1: Cassandra campaigns only. For other technologies the tab shows a
 * placeholder.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

const CassandraReportForCampaign = ({ assignedUsers }) => {
  const [connections, setConnections] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Privilege breakdown modal (opened by clicking a count in the table)
  const [modalData, setModalData] = useState(null);

  // Snapshot toggle: 'campaign' shows privileges as they stood at each user's
  // user_info_collected_date (i.e. when the campaign was generated), 'current'
  // shows the latest collected data. The toggle affects both the table counts
  // and the drill-down modal so both views are consistent.
  const [snapshot, setSnapshot] = useState('campaign');

  const assignedUsernames = useMemo(
    () => new Set(assignedUsers.map(u => u.db_username)),
    [assignedUsers]
  );
  const databaseNames = useMemo(
    () => [...new Set(assignedUsers.map(u => u.database_name))],
    [assignedUsers]
  );

  // Map username -> the campaign's collection timestamp for that user. Built from
  // the attester's assigned-users payload (uar_report row), so we don't need an
  // extra round-trip to fetch it.
  const campaignTsByUsername = useMemo(() => {
    const m = new Map();
    assignedUsers.forEach(u => {
      if (u.user_info_collected_date && !m.has(u.db_username)) {
        m.set(u.db_username, u.user_info_collected_date);
      }
    });
    return m;
  }, [assignedUsers]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(API_CONFIG.getDatabasesEndpoint('cassandra/source-connections/'));
        if (!cancelled) setConnections(resp.data.connections || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load Cassandra connections.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (connections.length === 0 || databaseNames.length === 0) return;
    let cancelled = false;

    const resolveConnectionId = (databaseName) => {
      const match = connections.find(c =>
        c.database_identifier === databaseName || c.cluster_name === databaseName
      );
      return match ? (match.connection_id || match.id) : null;
    };

    // For a given database, pick the campaign-creation timestamp used for its users.
    // All users assigned to one attester inside one database share the same collection
    // run, so any user in that database is representative.
    const campaignTsForDatabase = (databaseName) => {
      const u = assignedUsers.find(au => au.database_name === databaseName && au.user_info_collected_date);
      return u?.user_info_collected_date || null;
    };

    // Backend's cassandra-access/report/ uses strptime('%Y-%m-%d %H:%M:%S') — no
    // fractional seconds — and applies a 1-second window. Truncate to that format.
    // The DB stores UTC but the prepare/ endpoint serializes user_info_collected_date
    // without a timezone suffix (e.g. '2026-04-22T23:20:01.091177'), which the JS
    // Date constructor interprets as local time. Force UTC interpretation when no
    // timezone is present so the second-level window matches the DB row.
    const toReportDate = (iso) => {
      if (!iso) return null;
      const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
      const d = new Date(hasTz ? iso : `${iso}Z`);
      if (isNaN(d.getTime())) return null;
      const pad = n => String(n).padStart(2, '0');
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
    };

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const perDb = await Promise.all(
          databaseNames.map(async (databaseName) => {
            const connId = resolveConnectionId(databaseName);
            if (!connId) return [];
            const filters = {};
            if (snapshot === 'campaign') {
              const reportDate = toReportDate(campaignTsForDatabase(databaseName));
              if (reportDate) filters.collection_date = reportDate;
            }
            const resp = await axios.post(
              API_CONFIG.getDatabasesEndpoint('cassandra-access/report/'),
              { connection_id: connId, filters }
            );
            return (resp.data?.users || []).map(u => ({ ...u, database_name: databaseName, _connection_id: connId }));
          })
        );
        if (cancelled) return;
        const merged = perDb.flat().filter(u => assignedUsernames.has(u.username));
        setRows(merged);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Failed to load report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [connections, databaseNames, assignedUsernames, assignedUsers, snapshot]);

  // Open the drill-down for a given user + privilege bucket. Uses the same
  // cassandra-access/user-details/ endpoint the DBA-side report uses, with
  // identical filter logic so behaviour matches.
  const showPrivilegeDetails = async (user, privilegeType) => {
    try {
      if (privilegeType === 'roles') {
        setModalData({
          title: `Roles for ${user.username}`,
          type: 'roles',
          data: user.role_memberships || []
        });
        return;
      }

      const payload = { connection_id: user._connection_id, username: user.username };
      if (snapshot === 'campaign') {
        const campaignTs = campaignTsByUsername.get(user.username);
        if (campaignTs) {
          // Same UTC-normalization as toReportDate: the prepare/ endpoint drops the
          // timezone suffix, so tag it with Z so the backend parses it as UTC.
          const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(campaignTs);
          payload.collection_timestamp = hasTz ? campaignTs : `${campaignTs}Z`;
        }
      }
      const resp = await axios.post(
        API_CONFIG.getDatabasesEndpoint('cassandra-access/user-details/'),
        payload
      );
      const allPermissions = resp.data?.user?.all_permissions || [];

      const filtered = allPermissions.filter(perm => {
        const resource = perm.resource || '';
        const permissions = perm.permissions || [];
        const isElevated =
          resource === 'data' ||
          resource.startsWith('data/') ||
          resource.toLowerCase().includes('<keyspace') ||
          resource.toLowerCase().includes('<all');

        for (const p of permissions) {
          const u = p.toUpperCase();
          if (privilegeType === 'admin' && u === 'AUTHORIZE') return true;
          if (privilegeType === 'ddl' && ['CREATE', 'ALTER', 'DROP'].includes(u)) return true;
          if (privilegeType === 'write_elevated' && ['MODIFY', 'EXECUTE'].includes(u) && isElevated) return true;
          if (privilegeType === 'write' && ['MODIFY', 'EXECUTE'].includes(u) && !isElevated) return true;
          if (privilegeType === 'read_elevated' && ['SELECT', 'DESCRIBE'].includes(u) && isElevated) return true;
          if (privilegeType === 'read' && u === 'SELECT' && !isElevated) return true;
        }
        return false;
      });

      setModalData({
        title: `${privilegeType.toUpperCase().replace('_', ' ')} Privileges for ${user.username}`,
        type: privilegeType,
        data: filtered
      });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to fetch privilege details.');
    }
  };

  // Render a privilege count as a hyperlink when > 0, plain text "0" otherwise.
  const PrivLink = ({ count, user, type, color }) => {
    if (!count || count === 0) return <span>0</span>;
    return (
      <a
        href="#"
        onClick={(e) => { e.preventDefault(); showPrivilegeDetails(user, type); }}
        style={{ color: color || '#2563eb', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
      >
        {count}
      </a>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#667eea' }} />
        <p className="mt-3 text-muted">Loading user access report...</p>
      </div>
    );
  }
  if (error) {
    return <div className="alert alert-warning"><i className="fas fa-exclamation-triangle me-2" />{error}</div>;
  }
  if (rows.length === 0) {
    return <div className="text-muted py-4">No user access records found for your assigned users in this campaign.</div>;
  }

  return (
    <div>
      {/* Snapshot toggle: campaign creation vs. current */}
      <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
        <span className="text-muted" style={{ fontSize: '0.85rem', marginRight: 4 }}>
          <i className="fas fa-clock me-1" />Privileges:
        </span>
        {[
          { key: 'campaign', label: 'As of campaign creation' },
          { key: 'current',  label: 'Current' }
        ].map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setSnapshot(opt.key)}
            className={snapshot === opt.key ? 'btn btn-primary btn-sm' : 'btn btn-outline-secondary btn-sm'}
            style={{ fontSize: '0.8rem' }}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover table-sm table-bordered" style={{ fontSize: '0.85rem' }}>
          <thead className="table-dark">
            <tr>
              <th>Username</th>
              <th>Database</th>
              <th>Superuser</th>
              <th>Can Login</th>
              <th className="text-center">Roles</th>
              <th className="text-center">Admin</th>
              <th className="text-center">DDL</th>
              <th className="text-center">Write Elev.</th>
              <th className="text-center">Write</th>
              <th className="text-center">Read Elev.</th>
              <th className="text-center">Read</th>
              <th>Priv Level</th>
              <th>Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u, i) => (
              <tr key={i} className={u.is_superuser ? 'table-danger' : ''}>
                <td><code>{u.username}</code></td>
                <td>{u.database_name}</td>
                <td style={{ fontWeight: u.is_superuser ? 600 : 400, color: u.is_superuser ? '#dc2626' : 'inherit' }}>
                  {u.is_superuser ? 'YES' : 'NO'}
                </td>
                <td>{u.can_login ? 'YES' : 'NO'}</td>
                <td className="text-center"><PrivLink count={u.roles_granted}          user={u} type="roles" /></td>
                <td className="text-center"><PrivLink count={u.admin_privs}            user={u} type="admin"           color="#dc2626" /></td>
                <td className="text-center"><PrivLink count={u.ddl_privs}              user={u} type="ddl"             color="#dc2626" /></td>
                <td className="text-center"><PrivLink count={u.write_elevated_privs}   user={u} type="write_elevated"  color="#d97706" /></td>
                <td className="text-center"><PrivLink count={u.write_privs}            user={u} type="write" /></td>
                <td className="text-center"><PrivLink count={u.read_elevated_privs}    user={u} type="read_elevated"   color="#d97706" /></td>
                <td className="text-center"><PrivLink count={u.read_privs}             user={u} type="read" /></td>
                <td>{u.privilege_level_name || u.privilege_level}</td>
                <td>{u.risk_level || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Privilege breakdown modal — mirrors the DBA-side Cassandra report */}
      {modalData && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setModalData(null)}
        >
          <div
            style={{
              backgroundColor: 'white', padding: 30, borderRadius: 8,
              maxWidth: 860, width: '90%', maxHeight: '80vh', overflow: 'auto',
              boxShadow: '0 4px 18px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h5 style={{ margin: 0 }}>{modalData.title}</h5>
              <button
                onClick={() => setModalData(null)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#666' }}
                aria-label="Close"
              >×</button>
            </div>

            {modalData.type === 'roles' ? (
              modalData.data.length === 0 ? (
                <p className="text-muted">No roles granted.</p>
              ) : (
                <table className="table table-sm table-bordered">
                  <thead className="table-dark">
                    <tr><th>Role Name</th><th className="text-center">Depth</th></tr>
                  </thead>
                  <tbody>
                    {modalData.data.map((role, idx) => (
                      <tr key={idx}>
                        <td><code>{role.role}</code></td>
                        <td className="text-center">{role.depth}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : (
              modalData.data.length === 0 ? (
                <p className="text-muted">No privileges of this type.</p>
              ) : (
                <table className="table table-sm table-bordered">
                  <thead className="table-dark">
                    <tr>
                      <th>Resource</th>
                      <th>Permissions</th>
                      <th className="text-center">Source</th>
                      <th className="text-center">Grant Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.data.map((perm, idx) => (
                      <tr key={idx}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{perm.resource}</td>
                        <td>{(perm.permissions || []).join(', ')}</td>
                        <td className="text-center">{perm.source}</td>
                        <td className="text-center">
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            backgroundColor: perm.grant_type === 'DIRECT' ? '#dbeafe' : '#fef3c7',
                            color: perm.grant_type === 'DIRECT' ? '#1e40af' : '#92400e',
                            fontSize: 11,
                            fontWeight: 700
                          }}>
                            {perm.grant_type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const UserAccessReportTab = ({ users }) => {
  const campaigns = useMemo(() => {
    const byId = new Map();
    users.forEach(u => {
      if (!u.campaign_id) return;
      if (!byId.has(u.campaign_id)) {
        byId.set(u.campaign_id, {
          campaign_id: u.campaign_id,
          campaign_name: u.campaign_name,
          database_type: u.database_type,
          users: []
        });
      }
      byId.get(u.campaign_id).users.push(u);
    });
    return [...byId.values()].sort((a, b) => a.campaign_name.localeCompare(b.campaign_name));
  }, [users]);

  const [selectedId, setSelectedId] = useState(campaigns[0]?.campaign_id || '');

  useEffect(() => {
    if (campaigns.length > 0 && !campaigns.find(c => c.campaign_id === selectedId)) {
      setSelectedId(campaigns[0].campaign_id);
    }
  }, [campaigns, selectedId]);

  const selected = campaigns.find(c => c.campaign_id === selectedId);

  if (campaigns.length === 0) {
    return <div className="text-muted py-4">No campaigns available.</div>;
  }

  return (
    <div style={{ padding: 15 }}>
      <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
        <label style={{ fontWeight: 600, marginBottom: 0 }}>Campaign:</label>
        <select
          className="form-select"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          style={{ maxWidth: 480 }}
        >
          {campaigns.map(c => (
            <option key={c.campaign_id} value={c.campaign_id}>
              {c.campaign_name} ({c.database_type}) — {c.users.length} user{c.users.length === 1 ? '' : 's'}
            </option>
          ))}
        </select>
      </div>

      {selected?.database_type === 'CASSANDRA' ? (
        <CassandraReportForCampaign assignedUsers={selected.users} />
      ) : (
        <div className="alert alert-info">
          <i className="fas fa-info-circle me-2" />
          The User Access Report tab currently supports Cassandra campaigns only. Selected
          campaign uses <strong>{selected?.database_type}</strong>.
        </div>
      )}
    </div>
  );
};

export default UserAccessReportTab;
